<template>
  <div>
    <v-container fluid grid-list-md>
      <v-layout v-if="mode === 'list'" justify-space-between>
        <v-flex xs3 sm3 md3 d-flex>
          <div>
            <permission-tooltip
                :permission="hasPermission('prescription_create')"
                :tooltip="{right: true, bottom: false, attach: false}"
            >
              <prescription-manage-modal
                  @prescription_stored="fetchData"
                  :currentPrescription="currentPrescription"
                  :disabled="!hasPermission('prescription_create')"
              />
            </permission-tooltip>
          </div>
        </v-flex>
      </v-layout>
      <v-layout>
        <v-flex xs12 sm3 md3 d-flex>
          <div>
            <permission-tooltip
                :permission="hasPermission('prescription_list')"
                :tooltip="{top: true, bottom: false, attach: false}"
            >
              <v-text-field v-model="filters.search"
                            append-icon="search"
                            :label="trans('app.search.search')"
                            single-line
                            hide-details
                            solo
                            :disabled="!hasPermission('prescription_list')"
                            clearable
              ></v-text-field>
            </permission-tooltip>
          </div>
        </v-flex>
        <v-flex xs12 sm3 md3 d-flex>
          <div>
            <permission-tooltip
                :permission="hasPermission('prescription_list')"
                :tooltip="{top: true, bottom: false, attach: false}"
            >
              <v-combobox
                  clearable
                  :items="[{text: trans('app.own'), value: 'own'},{text: trans('app.system'), value: 'system'}, {text: trans('app.shared'), value: 'shared'}]"
                  :label="trans('app.database')"
                  v-model="filters.database"
                  hide-details
                  solo
              ></v-combobox>
            </permission-tooltip>
          </div>
        </v-flex>
      </v-layout>
    </v-container>
    <v-data-table
        :disable-initial-sort="true"
        :headers="headers"
        :items="items"
        :pagination.sync="pagination"
        :total-items="total"
        :loading="isLoading"
        :rows-per-page-items="[10,25,50,100]"
    >
      <v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>
      <template slot="items" slot-scope="props">
        <tr>
          <td class="text-xs-left">
            {{ props.item.name }}
            <icon-tooltip :enabled="props.item.system" :tooltip-text="trans('app.prescriptions.system_prescription')">
                <v-btn flat icon color="success" class="mr-0 ml-0">
                  <i class="logo-small"></i>
                </v-btn>
            </icon-tooltip>
            <icon-tooltip :tooltip-text="trans('app.sharing.shared_by', {name: props.item.owned_by})" :enabled="props.item.shared"
                          >
              <v-icon small color="success">share</v-icon>
            </icon-tooltip>
          </td>
          <td class="text-xs-center" v-if="mode === 'list'">
            <span v-if="!props.item.system">{{ props.item.created_at|format_date(FORMAT_DATETIME) }}</span>
          </td>
          <td class="justify-end layout px-0 pr-4">
            <v-layout align-center justify-end v-if="mode === 'list'">
              <icon-button
                  :permission="canEdit(props.item)"
                  icon="edit"
                  color="success"
                  cls="mr-0 ml-0"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
                  @click="openModal(props.item)"
              >
                {{ 'app.show_and_edit' | trans }}
              </icon-button>
              <icon-button
                  @click="copy(props.item)"
                  icon="file_copy"
                  color="light-blue"
                  cls="mr-0 ml-0"
                  :permission="canCopy(props.item)"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
              >
                {{ 'app.copy' | trans }}
              </icon-button>
              <icon-button
                  v-if="canDelete(props.item)"
                  :permission="canDelete(props.item)"
                  icon="delete"
                  color="red"
                  cls="mr-0 ml-0"
                  :tooltip="{top: true, bottom: false, attach: false}"
                  :iconOptions="{small: true}"
                  @click="deletePrescription(props.item)"
              >
                {{ 'app.delete' | trans }}
              </icon-button>
            </v-layout>
            <v-layout align-center justify-end v-else-if="mode === 'select'">
              <permission-button
                  :permission="props.item.system ? 'prescription_system_show' : 'prescription_show'"
                  :color="`primary`"
                  :tooltip="{right: true, bottom: false, attach: false}"
                  @click="selectPrescription(props.item)"
              >
                {{ 'app.select' | trans }}
              </permission-button>
            </v-layout>
          </td>
        </tr>
      </template>
      <template v-slot:no-data>
        <div class="text-center">
                <span v-if="(filters.database && filters.database.value === 'shared') && !canUseSharing" v-html="trans('app.no_access_tooltip')">
                </span>
          <span v-else>
                  {{ trans('app.search.no_data') }}
                </span>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import {trans} from "../../../app";
import {stringify} from "qs";
import debounce from "lodash/debounce";
import goTo from 'vuetify/lib/components/Vuetify/goTo';
import {mapGetters} from 'vuex'
import {FORMAT_DATETIME} from "../../../utils/dateFormatter";

export default {
  props: {
    mode: {
      type: String,
      default: 'list',
    },
  },
  data: function () {
    return {
      FORMAT_DATETIME: FORMAT_DATETIME,
      currentPrescription: null,
      items: [],
      pagination: {
        rowsPerPage: this.mode === 'list' ? 50 : 10,
        sortBy: null,
        descending: false,
      },
      total: 0,
      isLoading: true,
      filters: {
        search: null,
        database: null,
      },
      parser: new DOMParser(),
    }
  },
  computed: {
    ...mapGetters(['hasPermission', 'canUseSharing', 'excludedPrescriptionIds']),
    headers() {
      if (this.mode === 'list') {
        return [
          {
            text: trans('app.name'), align: 'left', value: 'name', sortable: true,
          },
          {
            text: trans('app.createdAt'), align: 'center', value: 'created_at', sortable: true,
          },
          {
            text: '', align: 'right', sortable: false,
          },
        ];
      } else if (this.mode === 'select') {
        return [
          {
            text: trans('app.name'), align: 'left', value: 'name', sortable: true,
          },
          {
            text: '', align: 'right', sortable: false,
          },
        ];
      } else {
        return []
      }
    },

  },
  watch: {
    filters: {
      handler() {
        this.debounceFetch();
      },
      deep: true,
    },
    pagination: {
      handler(newValue, oldValue) {
        if (oldValue.page !== undefined) {
          this.fetchData();
        }
      },
      deep: true,
    },
  },
  created() {
    this.isLoading = this.isLoading && this.hasPermission('prescription_list');
    this.debounceFetch = debounce(this.fetchData, 300);
  },
  methods: {
    resetSearch() {
      this.filters = {
        search: null,
        database: null,
      };
      this.pagination.sortBy = null;
      this.pagination.descending = false;
    },
    async fetchData() {
      this.isLoading = true;
      const {sortBy, descending, page, rowsPerPage} = this.pagination;

      let query = {
        page: page,
        perPage: rowsPerPage,
        orderBy: sortBy,
        dir: descending ? 'desc' : 'asc',
        excludedPrescriptionIds: this.excludedPrescriptionIds,
        ...this.filters,
      };

      if (this.filters.database) {
        query.database = this.filters.database.value;
      }

      const response = await axios.get(`/v2/prescriptions?${stringify(query, {skipNulls: true})}`);
      const {data, meta} = response.data;
      this.items = data;
      this.total = meta.total;
      this.pages = meta.last_page;
      this.isLoading = false;
    },
    openModal(item) {
      this.currentPrescription = Object.assign({}, item);
    },
    deletePrescription(item) {
      this.$confirmDanger(
          trans(item.sent_to_mobile_app ? 'app.confirm_prescription_removal_mobile_app': 'app.confirm_prescription_removal', {name: item.name}),
          trans('app.remove_prescription')
      ).then(async () => {
        goTo(0, {
          duration: 1000
        });
        await axios.delete(`/v2/prescriptions/${item.id}`);
        await this.fetchData();
        this.$toast(trans('app.actions.removed', {name: trans('app.prescription_accusative')}));
      }).catch((error) => {
        this.sentryCaptureException(error);
      })
    },
    async copy(item) {
      await axios.put(`/v2/prescriptions/${item.id}/copy`);
      await this.fetchData();
      this.$toast(trans('app.actions.copied', {name: trans('app.prescription_accusative')}));
    },
    showSystem(item) {
      this.openModal(item)
    },
    selectPrescription(item) {
      this.$emit('prescription-selected', item);
    },
    canEdit(item) {
      if (item.system && (this.hasPermission('prescription_system_edit') || this.hasPermission('prescription_system_show'))) {
        return true;
      }
      return !item.system && this.hasPermission('prescription_edit');
    },
    canCopy(item) {
      if (item.system && this.hasPermission('prescription_system_show')) {
        return true;
      }
      return !item.system && this.hasPermission('prescription_copy');
    },
    canDelete(item) {
      if (item.system && this.hasPermission('prescription_system_delete')) {
        return true;
      }
      return !item.shared && !item.system && this.hasPermission('prescription_delete');
    },
  }
}
</script>
